import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppBar, Box, IconButton, Toolbar, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LanguageButton from "./LanguageButton";
import ModalGeneric from "./ModalGeneric";
import { appTopBarSx, APP_HEADER_HEIGHT } from "../../Utils/pageStyles";

export default function AppTopBar({
  config,
  onLogout,
  onLanguageChange,
  drawerOpen,
  setDrawerOpen,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const primary = theme.palette.primary.main;
  const nav = theme.palette.nav?.main ?? "#000922";

  const handleLogoutClick = () => setLogoutModalOpen(true);

  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    setDrawerOpen(false);
    onLogout();
    navigate("/Account/Login");
  };

  return (
    <>
      <ModalGeneric
        open={logoutModalOpen}
        title={t("navigation.logout")}
        description={t("navigation.logoutConfirm")}
        onClose={() => setLogoutModalOpen(false)}
        onAccept={handleConfirmLogout}
        bodyComponent={<></>}
      />

      <AppBar
        position="static"
        elevation={0}
        sx={{
          flexShrink: 0,
          width: "100%",
          maxWidth: "100%",
          ...appTopBarSx,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            minHeight: APP_HEADER_HEIGHT,
            height: APP_HEADER_HEIGHT,
            px: { xs: 1.25, sm: 2 },
            gap: 1,
            boxSizing: "border-box",
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            aria-label={
              drawerOpen ? t("navigation.closeMenu") : t("navigation.openMenu")
            }
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              p: 0,
              color: "#fff",
              borderRadius: 1.5,
              bgcolor: primary,
              boxShadow: `0 4px 12px rgba(7, 70, 142, 0.28)`,
              "&:hover": { bgcolor: nav },
            }}
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.75, sm: 1.5 },
              flexShrink: 0,
              height: APP_HEADER_HEIGHT,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <LanguageButton
                config={config}
                onLanguageChange={onLanguageChange}
              />
            </Box>

            <IconButton
              onClick={handleLogoutClick}
              aria-label={t("navigation.logoutAriaLabel")}
              title={t("navigation.logout")}
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: 2,
                border: "1px solid rgba(7, 70, 142, 0.35)",
                color: primary,
                "&:hover": {
                  borderColor: primary,
                  bgcolor: "rgba(7, 70, 142, 0.06)",
                },
              }}
            >
              <LogoutOutlinedIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}
